import React from "react";
import {
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
  Box,
} from "@mui/material";
import { CalendarToday } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const formatIndianCurrency = (value) => {
  if (!value) return "";
  const num = parseInt(value.replace(/,/g, ""), 10);
  return isNaN(num) ? value : num.toLocaleString("en-IN");
};

const renderField = ({ field, value, error, editMode, isSaving, handleChange }) => {
  const isDisabled = field.name === "mobile_number";

  // 🎯 Date of Birth
  if (field.name === "dob") {
    return (
      <Grid item xs={12} sm={6} key={field.name}>
        {editMode ? (
          <DatePicker
            label={field.label}
            value={value ? dayjs(value) : null}
            onChange={(date) =>
              handleChange(field.name, dayjs(date).format("YYYY-MM-DD"))
            }
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
        ) : (
          <Box className="field-box">
            <Typography className="field-label">{field.label}</Typography>
            <Typography className="field-value">
              {value ? dayjs(value).format("DD/MM/YYYY") : "Not Provided"}
            </Typography>
          </Box>
        )}
      </Grid>
    );
  }

  // 🎯 Gender
  if (field.name === "gender") {
    return (
      <Grid item xs={12} sm={6} key={field.name}>
        {editMode ? (
          <TextField
            select
            fullWidth
            size="small"
            label={field.label}
            value={value === "M" ? "Male" : value === "F" ? "Female" : value}
            onChange={(e) => handleChange(field.name, e.target.value)}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </TextField>
        ) : (
          <Box className="field-box">
            <Typography className="field-label">{field.label}</Typography>
            <Typography className="field-value">
              {value === "M" ? "Male" : value === "F" ? "Female" : "Not Provided"}
            </Typography>
          </Box>
        )}
      </Grid>
    );
  }

  // 🎯 Monthly Income
  if (field.name === "monthly_income" || field.name === "req_loan_amt") {
    return (
      <Grid item xs={12} sm={6} key={field.name}>
        {editMode ? (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            value={formatIndianCurrency(value)}
            onChange={(e) =>
              handleChange(field.name, e.target.value.replace(/,/g, ""))
            }
          />
        ) : (
          <Box className="field-box">
            <Typography className="field-label">{field.label}</Typography>
            <Typography className="field-value">
              ₹ {formatIndianCurrency(value)}
            </Typography>
          </Box>
        )}
      </Grid>
    );
  }

  // 🎯 Loan Type
  if (field.name === "loan_type") {
    return (
      <Grid item xs={12} sm={6} key={field.name}>
        {editMode ? (
          <TextField
            select
            fullWidth
            size="small"
            label={field.label}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
          >
            <MenuItem value="PL">PL</MenuItem>
            <MenuItem value="BL">BL</MenuItem>
            <MenuItem value="AL">AL</MenuItem>
            <MenuItem value="HL">HL</MenuItem>
            <MenuItem value="LAP">LAP</MenuItem>
          </TextField>
        ) : (
          <Box className="field-box">
            <Typography className="field-label">{field.label}</Typography>
            <Typography className="field-value">{value || "Not Provided"}</Typography>
          </Box>
        )}
      </Grid>
    );
  }

  // 🎯 Tenure
  if (field.name === "tenure") {
    return (
      <Grid item xs={12} sm={6} key={field.name}>
        {editMode ? (
          <TextField
            type="number"
            fullWidth
            size="small"
            label={field.label}
            value={value}
            onChange={(e) => {
              const val = Math.min(parseInt(e.target.value || "0", 10), 84);
              handleChange(field.name, val.toString());
            }}
            inputProps={{ max: 84 }}
          />
        ) : (
          <Box className="field-box">
            <Typography className="field-label">{field.label}</Typography>
            <Typography className="field-value">{value || "Not Provided"}</Typography>
          </Box>
        )}
      </Grid>
    );
  }

  // 🎯 Employment Type
  if (field.name === "employment") {
    return (
      <Grid item xs={12} sm={6} key={field.name}>
        {editMode ? (
          <TextField
            select
            fullWidth
            size="small"
            label={field.label}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
          >
            <MenuItem value="Salaried">Salaried</MenuItem>
            <MenuItem value="Self Employed">Self Employed</MenuItem>
            <MenuItem value="Business">Business</MenuItem>
          </TextField>
        ) : (
          <Box className="field-box">
            <Typography className="field-label">{field.label}</Typography>
            <Typography className="field-value">{value || "Not Provided"}</Typography>
          </Box>
        )}
      </Grid>
    );
  }

  // 🎯 Default Field
  return (
    <Grid item xs={12} sm={6} key={field.name}>
      {editMode ? (
        <TextField
          fullWidth
          size="small"
          label={field.label}
          value={value}
          onChange={(e) => handleChange(field.name, e.target.value)}
          disabled={isDisabled || isSaving}
          error={!!error}
          helperText={error}
        />
      ) : (
        <Box className="field-box">
          <Typography className="field-label">{field.label}</Typography>
          <Typography className="field-value">{value || "Not Provided"}</Typography>
        </Box>
      )}
    </Grid>
  );
};

export default renderField;